/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiGetScreening } from '@/api/services';
import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { Screening } from '@/types/Screening';

import CreateUpdateAgentIndex from './CreateUpdateAgentIndex';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';

export default function UpdateScreeningPage() {
  const { screeningId } = useParams<{ screeningId: string }>();
  const navigate = useNavigate();
  const [isLoadingScreening, setIsLoadingScreening] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { initializeFromScreening, reset } = useCreateInterviewAgent();

  const { parseTimezone } = useTimezoneSelect({
    timezones: allTimezones,
    labelStyle: 'original'
  });

  // Fetch and initialize screening data
  useEffect(() => {
    const fetchScreening = async () => {
      if (!screeningId) {
        navigate('/interview');
        return;
      }

      setIsLoadingScreening(true);
      setFetchError(null);
      try {
        const response = await fetchAPI<Screening>({
          request: () => apiGetScreening(screeningId)
        });

        if (response.success) {
          const timezone = parseTimezone(response.data.deadline_timezone);
          initializeFromScreening(response.data, timezone);
        } else {
          console.error('Failed to fetch screening details:', response.message);
          setFetchError('Failed to load screening data. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching screening details:', error);
        setFetchError('Failed to load screening data. Please try again.');
      } finally {
        setIsLoadingScreening(false);
      }
    };

    fetchScreening();

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [screeningId]);

  const handleUpdateSuccess = useCallback(() => {
    // Navigate back to interview list after successful update
    navigate('/interview');
  }, [navigate]);

  const handleRetryFetch = async () => {
    if (!screeningId) return;

    setIsLoadingScreening(true);
    setFetchError(null);
    try {
      const response = await fetchAPI<Screening>({
        request: () => apiGetScreening(screeningId)
      });

      if (response.success) {
        const timezone = parseTimezone(response.data.deadline_timezone);
        initializeFromScreening(response.data, timezone);
      } else {
        setFetchError(response.message || 'Failed to fetch screening details');
      }
    } catch (error) {
      console.error('Error fetching screening details:', error);
      setFetchError('Failed to load screening data. Please try again.');
    } finally {
      setIsLoadingScreening(false);
    }
  };

  if (isLoadingScreening) {
    return (
      <Flex justify="center" align="center" minH="60vh" w="100%">
        <Spinner size="lg" color="primary.900" />
      </Flex>
    );
  }

  if (fetchError) {
    return (
      <Flex justify="center" w="100%" align="center" minH="60vh" direction="column" gap="16px">
        <Box color="error.700" fontSize="16px" textAlign="center" role="alert">
          {fetchError}
        </Box>
        <Button
          variant="outline"
          size="lg"
          borderColor="primary.900"
          color="primary.900"
          borderRadius="8px"
          _hover={{ bg: 'neutrals.50' }}
          onClick={handleRetryFetch}
        >
          Try Again
        </Button>
      </Flex>
    );
  }

  return <CreateUpdateAgentIndex onUpdateSuccess={handleUpdateSuccess} />;
}
